<script lang="ts" setup>
import {
  DataPoint,
  Groups,
  LoadingSpaceDimensions,
  TechDataVehicle,
  VehicleDimensions,
  Weights,
  Body,
} from '@/types';
import {
  accordionNames,
  bodyValues,
  loadingSpaceDimensionsValues,
  vehicleDimensionsValues,
  weightsValues,
} from '../utils/accordionLabels';
import TableValuesItem from '@/components/table-values-item.vue';
import { getDefaultLocaleValue } from '../locales/default';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import TechDataModalHeader from './tech-data-modal-header.vue';
import { useTracking } from '../composables/useTracking';

const props = defineProps<{
  translations: Record<string, string>;
  vehicle: TechDataVehicle;
  isOpen: boolean;
  locale: string;
  modelSeries: string;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const animatingIn = ref(false);
let lastOpenedByEvent: EventTarget | null = null;
let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

// Tracking
const { trackModalScroll } = useTracking(props.modelSeries);

const tableWrapperRef = ref<HTMLElement | null>(null);
let hasTrackedScrollEnd = false;

watch(
  () => props.isOpen,
  (open) => {
    if (open && dialogRef.value?.open !== true) {
      lastOpenedByEvent = document.activeElement;
      document.body.style.overflow = 'hidden';

      dialogRef.value?.showModal?.();
    } else {
      document.body.style.overflow = '';
    }

    requestAnimationFrame(() => (animatingIn.value = true));

    if (open) {
      hasTrackedScrollEnd = false;
      if (tableWrapperRef.value) {
        tableWrapperRef.value.addEventListener(
          'scroll',
          throttledScrollHandler,
        );
      }
    } else {
      if (tableWrapperRef.value) {
        tableWrapperRef.value.removeEventListener(
          'scroll',
          throttledScrollHandler,
        );
      }
    }
  },
);

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    emit('close');
  }
}

function handleOutsideClick(event: MouseEvent) {
  const dialog = dialogRef.value;
  if (!dialog) return;

  if (event.target === lastOpenedByEvent) {
    lastOpenedByEvent = null;
    return;
  }

  const rect = dialog.getBoundingClientRect();
  const clickInside =
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom;

  if (!clickInside && props.isOpen) {
    dialog.close();
    emit('close');
  }
}

function throttle(func, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall >= delay) {
      lastCall = now;
      return func(...args);
    }
  };
}

function handleScroll() {
  const el = tableWrapperRef.value;
  if (!el || hasTrackedScrollEnd) return;

  const checkIfAtBottom = () => {
    if (hasTrackedScrollEnd) return; // Prevent double firing
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;
    const scrollHeight = el.scrollHeight;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      trackModalScroll('view_full_modal', props.vehicle);
      hasTrackedScrollEnd = true;
    }
  };

  // Check immediately (throttled)
  checkIfAtBottom();

  // checking shortly after scroll ends because scroll event may not fire depending on scrolling speed (e.g. fast scroll)
  if (scrollTimeout) clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    checkIfAtBottom();
  }, 150);
}

const throttledScrollHandler = throttle(handleScroll, 50);

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('click', handleOutsideClick);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('click', handleOutsideClick);

  // Clean up scroll listener fallback
  if (tableWrapperRef.value) {
    tableWrapperRef.value.removeEventListener('scroll', throttledScrollHandler);
  }
  // Clear scroll timeout if it exists
  if (scrollTimeout) clearTimeout(scrollTimeout);
});

const computedAccordions = computed(() =>
  accordionNames
    .map((accordion) => props.vehicle[accordion as keyof Groups])
    .filter((accordion) => accordion && !areAllKeysNull(accordion)),
);

const accordionLabels = computed(() =>
  Object.keys(props.vehicle)
    .filter((key) => !areAllKeysNull(props.vehicle[key as keyof Groups]))
    .filter((key) => accordionNames.includes(key)),
);

function areAllKeysNull(obj: any) {
  for (let key in obj) {
    if (key !== 'label' && obj.hasOwnProperty(key) && obj[key] !== null) {
      return false;
    }
  }
  return true;
}

function getDataPoints(
  accordion:
    | VehicleDimensions
    | LoadingSpaceDimensions
    | Weights
    | Body
    | undefined,
) {
  const dataPoints: DataPoint[] = [];
  const valueGroups = [
    { values: weightsValues, accordion: accordion as Weights },
    { values: bodyValues, accordion: accordion as Body },
    {
      values: loadingSpaceDimensionsValues,
      accordion: accordion as LoadingSpaceDimensions,
    },
    {
      values: vehicleDimensionsValues,
      accordion: accordion as VehicleDimensions,
    },
  ];

  valueGroups.forEach(({ values, accordion }) => {
    if (Object.keys(accordion).some((key) => values.includes(key))) {
      Object.values(accordion).forEach((dataPoint) => {
        if (dataPoint) {
          dataPoints.push(dataPoint);
        }
      });
    }
  });

  return dataPoints;
}

function getStatLabels(accordion: object): string[] {
  return Object.keys(accordion);
}
</script>

<template>
  <dialog
    v-show="isOpen"
    ref="dialogRef"
    role="dialog"
    aria-modal="true"
    aria-labelledby="tech-data-modal-title"
    class="owvans-modal"
    :class="{ '-animate-in': animatingIn }"
    @close.capture.stop="emit('close')"
  >
    <TechDataModalHeader
      :translations="translations"
      :vehicle="vehicle"
      :validOptions="validOptions"
      :close="
        () => {
          dialogRef?.close();
          emit('close');
        }
      "
    />
    <div class="owvans-table-wrapper" ref="tableWrapperRef">
      <table
        v-for="(accordion, accordionIndex) in computedAccordions"
        :key="accordionIndex"
        class="wbx-table"
      >
        <thead>
          <tr>
            <th scope="col" :id="`tech-data-modal-title-${accordionIndex}`">
              {{
                props.translations[
                  `owvc_tech_data_${accordionLabels[accordionIndex]}`
                ] || getDefaultLocaleValue(accordionLabels[accordionIndex])
              }}
            </th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          <TableValuesItem
            v-for="(dataPoint, key) in getDataPoints(accordion)"
            :key="key"
            :data-point="dataPoint"
            :locale="locale"
            :parentLabel="accordionLabels[accordionIndex]"
            :label="getStatLabels(accordion ?? {})[key]"
            :translations="translations"
          />
        </tbody>
      </table>
    </div>
  </dialog>
</template>

<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';

.owvans-modal {
  --dialog-max-height: 800px;

  position: fixed;
  z-index: 1000;

  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  background-color: var(--wb-data-table-background-main-color);
  border: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transition:
    transform var(--_duration) var(--wb-move),
    opacity var(--_duration) var(--wb-fade);

  inset: 0;
  transform: translateY(50%);

  max-width: var(--wb-grid-width);
  max-height: var(--dialog-max-height);

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(2px);
    transition: opacity 0.3s ease;
  }

  &.-animate-in {
    opacity: 1;
    transform: translateX(0);
    &::backdrop {
      opacity: 1;
    }
  }
}

.owvans-table-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.wbx-table {
  width: 100%;
  margin-bottom: 24px;
}

:deep(.wbx-table th) {
  padding-top: 24px;
  padding-bottom: 24px;
}

th {
  width: 100%;
}

thead {
  background-color: var(--wb-table-cell-background-striped-color);
}
</style>
